import type { YaasPositionCardData, YaasWalletStats } from "@solomon-apps/shared";

import {
    DEFAULT_RETAIL_MIN_BALANCE_USDV,
    DEFAULT_RETAIL_MIN_YIELD_PER_DISTRIBUTION_USDV
} from "@/config/retailYaasPolicy";

export const MOCK_WALLET_STATS: YaasWalletStats = {
    totalPositions: 12450.75,
    formattedTotalPositions: "$12,450.75",
    totalYieldEarned: 1247.32,
    formattedTotalYieldEarned: "$1,247.32",
    totalTvl: 3_000_000,
    formattedTvl: "$3.0M"
};

export const MOCK_NEXT_DISTRIBUTION = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString();

export const MOCK_ACTIVE_POSITION: YaasPositionCardData = {
    id: 1,
    uuid: "00000000-0000-4000-8000-000000000001",
    pool: { pair: "USDv", protocol: "Solomon", type: "Wallet" },
    position: {
        lpAddress: "EnViUgEjqL6HzLRxLDnxTb4t5xukumJYpNwq6fxF1tJYY",
        USDvValue: 12450.75,
        formattedUSDvValue: "$12,450.75",
        apy: 10.5,
        formattedApy: "10.5%",
        yaasApy: 10.5,
        formattedYaasApy: "10.5%",
        status: "Earning",
        isActive: true
    },
    rewards: {
        destination_address: "EnViUgEjqL6HzLRxLDnxTb4t5xukumJYpNwq6fxF1tJYY",
        rewards_amount: "$1,247.32"
    },
    splits: []
};

// A GOLD-USDv LP on Raydium, the shape transformPosition produces for a
// DEX_LP pool. Its own USDv value carries the position — the wallet
// balance overlay only touches Treasury / Wallet rows.
export const MOCK_GOLD_RAYDIUM_LP_POSITION: YaasPositionCardData = {
    ...MOCK_ACTIVE_POSITION,
    id: 2,
    uuid: "00000000-0000-4000-8000-000000000002",
    pool: { pair: "GOLD / USDv", protocol: "Raydium", type: "DEX LP" },
    position: {
        ...MOCK_ACTIVE_POSITION.position,
        // Placeholder pool address for the row's link-out. Swap for the
        // real Raydium GOLD-USDv pool once it exists.
        lpAddress: "GoLDUSDvRaydiumPooLPLACEHoLDER111111111111",
        USDvValue: 150_000,
        formattedUSDvValue: "$150,000.00"
    }
};

export const MOCK_GOLD_LP_WALLET_STATS: YaasWalletStats = {
    ...MOCK_WALLET_STATS,
    totalPositions: 150_000,
    formattedTotalPositions: "$150,000.00"
};

// The USDv held in the wallet beside the GOLD-USDv LP (gold-lp-wallet).
export const MOCK_GOLD_WALLET_POSITION: YaasPositionCardData = {
    ...MOCK_ACTIVE_POSITION,
    position: {
        ...MOCK_ACTIVE_POSITION.position,
        USDvValue: 10_500.37,
        formattedUSDvValue: "$10,500.37"
    }
};

export const MOCK_GOLD_LP_WALLET_WALLET_STATS: YaasWalletStats = {
    ...MOCK_WALLET_STATS,
    totalPositions: 160_500.37,
    formattedTotalPositions: "$160,500.37"
};

export const MOCK_PENDING_POSITION: YaasPositionCardData = {
    ...MOCK_ACTIVE_POSITION,
    position: {
        ...MOCK_ACTIVE_POSITION.position,
        status: "Pending",
        isActive: false
    },
    rewards: { ...MOCK_ACTIVE_POSITION.rewards, rewards_amount: "$0.00" }
};

// A day-zero real Program Position with no accrued rewards. This mock is for
// position demos only. Retail enrollment does not create it.
export const MOCK_OPTED_IN_POSITION: YaasPositionCardData = {
    ...MOCK_ACTIVE_POSITION,
    position: {
        ...MOCK_ACTIVE_POSITION.position,
        status: "Earning",
        isActive: true
    },
    rewards: { ...MOCK_ACTIVE_POSITION.rewards, rewards_amount: "$0.00" }
};

// Wallet stats for the just-opted-in frame. Same balance as the
// standard mock, but lifetime yield is zero — a fresh enrollment that
// inherited $1,247.32 of earnings would read as a bug in review.
export const MOCK_OPTED_IN_WALLET_STATS: YaasWalletStats = {
    ...MOCK_WALLET_STATS,
    totalYieldEarned: 0,
    formattedTotalYieldEarned: "$0.00"
};

// Demo thresholds follow the temporary frontend policy defaults.
export const MOCK_MIN_BALANCE = DEFAULT_RETAIL_MIN_BALANCE_USDV;
export const MOCK_MIN_YIELD_PER_DISTRIBUTION = DEFAULT_RETAIL_MIN_YIELD_PER_DISTRIBUTION_USDV;

// Mock connected-wallet address used as the pre-fill for the simple
// (Mode 2) per-position routing form's RewardsDestinationField.
export const MOCK_CONNECTED_WALLET = "E9Ytfvreri1KbHhE84J7UQoPjphL4KLC1ZwfE8PXWi7x";

// Active position whose balance has dropped below MOCK_MIN_BALANCE.
// The API status stays "Earning" on the underlying record — the API has
// no notion of this — so every surface derives the state from the
// balance against `minBalance` instead: the Error status badge, the
// below-minimum explainer and the Buy More USDv action all key off that
// one comparison.
//
// The balance MUST stay under MOCK_MIN_BALANCE for this fixture to mean
// anything. It previously read $42.18 against a $10 minimum, four times
// over the line it claimed to be under, which was harmless only because
// a separate hardcoded `pausedReason` flag drove the state instead.
export const MOCK_BELOW_MIN_POSITION: YaasPositionCardData = {
    ...MOCK_ACTIVE_POSITION,
    position: {
        ...MOCK_ACTIVE_POSITION.position,
        USDvValue: 4.18,
        formattedUSDvValue: "$4.18"
    }
};

// Disconnected-state ghost-card preview balance — round, illustrative
// number to anchor the earnings projection ("Holding $100,000 earns
// you $X per year"). Used purely as a marketing visual since the
// user isn't connected and we have no real wallet to read from.
export const MOCK_DISCONNECTED_POSITION: YaasPositionCardData = {
    ...MOCK_ACTIVE_POSITION,
    position: {
        ...MOCK_ACTIVE_POSITION.position,
        USDvValue: 100_000,
        formattedUSDvValue: "$100,000"
    }
};
